import { useEffect, useState } from 'react';
import { apiGet } from '@/api/api';

interface UserSessionCount {
  userId: number;
  sessionCount: number;
  hasData: boolean;
}

/**
 * 여러 사용자의 세션 개수를 백그라운드에서 조회하는 Hook
 * 대량의 사용자 조회 시 부하 분산을 위해 청크 단위로 처리
 */
export const useUserSessionCounts = (userIds: number[]) => {
  const [sessionCounts, setSessionCounts] = useState<Map<number, UserSessionCount>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (userIds.length === 0) return;

    const fetchSessionCounts = async () => {
      setIsLoading(true);
      const counts = new Map<number, UserSessionCount>();
      
      // 동시 요청 제한을 위한 청크 처리 (한 번에 5개씩)
      const chunkSize = 5;
      for (let i = 0; i < userIds.length; i += chunkSize) {
        const chunk = userIds.slice(i, i + chunkSize);
        
        const promises = chunk.map(async (userId) => {
          try {
            const response = await apiGet<any>(
              `/protectee/${userId}/mind-health/sessions?status=COMPLETED&pageSize=1`
            );
            
            const totalEntries = response.data?.pagination?.totalEntries || 0;
            
            return {
              userId,
              sessionCount: totalEntries,
              hasData: totalEntries > 0,
            };
          } catch (error) {
            console.error(`사용자 ${userId}의 세션 조회 실패:`, error);
            return {
              userId,
              sessionCount: 0,
              hasData: false,
            };
          }
        });

        const results = await Promise.all(promises);
        results.forEach(result => {
          counts.set(result.userId, result);
        });

        // 진행 상황을 중간 업데이트하고 싶다면 여기서 setSessionCounts 호출 가능
        // 하지만 렌더링 최적화를 위해 다 끝난 후 하거나, 일정 간격으로 하는 것이 좋음
        // 여기서는 청크가 많을 경우 사용자 경험을 위해 중간 업데이트를 수행 (선택사항)
        if (userIds.length > 20) { // 사용자가 많을 때만 중간 업데이트
             setSessionCounts(new Map(counts));
        }
      }

      setSessionCounts(counts);
      setIsLoading(false);
    };

    // 디바운스: 500ms 후에 조회
    const timeoutId = setTimeout(fetchSessionCounts, 500);
    return () => clearTimeout(timeoutId);
  }, [userIds.join(',')]); // ID 목록이 변경될 때만 재실행

  return { sessionCounts, isLoading };
};

